import { useState } from "react";
import { requestJSON, type AuthSession } from "../api";
import { useI18n } from "../i18n";
import { ManagementDialog, ManagementMessages } from "../managementUI";
import type { NotificationChannel } from "./NotificationChannels";

const generic = JSON.stringify(
  {
    status: "{{status}}",
    title: "{{title}}",
    summary: "{{summary}}",
    data_center_id: "{{data_center_id}}",
  },
  null,
  2,
);
const wecom = JSON.stringify(
  {
    msgtype: "text",
    text: {
      content: "[{{status}}] {{title}}\nDC: {{data_center_id}}\n{{summary}}",
    },
  },
  null,
  2,
);
export function NotificationEditor({
  channel,
  session,
  onClose,
  onSaved,
}: {
  channel: NotificationChannel | null;
  session: AuthSession;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(channel?.name || "");
  const [kind, setKind] = useState(channel?.kind || "webhook");
  const [replace, setReplace] = useState(!channel);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [severity, setSeverity] = useState(channel?.matches.severity || "");
  const [team, setTeam] = useState(channel?.matches.team_id || "");
  const [alertName, setAlertName] = useState(channel?.matches.alert_name || "");
  const [resolved, setResolved] = useState(channel?.send_resolved ?? true);
  const [url, setURL] = useState("");
  const [headers, setHeaders] = useState("{}");
  const [payload, setPayload] = useState(generic);
  const [successPath, setSuccessPath] = useState("");
  const [successValue, setSuccessValue] = useState("0");
  const [host, setHost] = useState("");
  const [port, setPort] = useState(587);
  const [tls, setTLS] = useState("starttls");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  async function save() {
    setBusy(true);
    setError("");
    try {
      const matches: Record<string, string> = {};
      if (severity) matches.severity = severity;
      if (team.trim()) matches.team_id = team.trim();
      if (alertName.trim()) matches.alert_name = alertName.trim();
      let configuration;
      if (replace) {
        configuration =
          kind === "email"
            ? {
                kind: "email",
                email: {
                  host,
                  port,
                  tls,
                  username,
                  password,
                  from,
                  to: to
                    .split(/[;,\n]/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                },
              }
            : {
                kind: "webhook",
                webhook: {
                  url,
                  headers: JSON.parse(headers),
                  payload: JSON.parse(payload),
                  success: successPath
                    ? { path: successPath, equals: JSON.parse(successValue) }
                    : undefined,
                },
              };
      }
      await requestJSON(
        "/api/v1/monitoring/notification-channels" +
          (channel ? "/" + channel.id : ""),
        {
          method: channel ? "PUT" : "POST",
          csrfToken: session.csrf_token,
          reason,
          body: {
            name,
            enabled: channel?.enabled || false,
            expected_revision: channel?.revision || 0,
            matches,
            send_resolved: resolved,
            configuration,
          },
        },
      );
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <ManagementDialog
      title={
        channel
          ? t("编辑通知渠道", "Edit notification channel")
          : t("添加通知渠道", "Add notification channel")
      }
      disabled={busy}
      onClose={onClose}
    >
      <form
        className="configuration-form-body configuration-form-two-column"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        autoComplete="off"
      >
        <div className="configuration-submit-wide">
          <ManagementMessages error={error} />
        </div>
        <label>
          {t("渠道名称", "Channel name")}
          <input
            required
            maxLength={128}
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          {t("类型", "Type")}
          <select
            disabled={busy || (!!channel && !replace)}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              if (e.target.value === "wecom") {
                setPayload(wecom);
                setSuccessPath("errcode");
                setSuccessValue("0");
              }
            }}
          >
            <option value="email">{t("邮件", "Email")}</option>
            <option value="webhook">Webhook</option>
            <option value="wecom">{t("企业微信机器人", "WeCom bot")}</option>
          </select>
        </label>
        {channel ? (
          <label className="checkbox-label configuration-submit-wide">
            <input
              type="checkbox"
              checked={replace}
              disabled={busy}
              onChange={(e) => setReplace(e.target.checked)}
            />
            {t(
              "重新填写接收配置（未勾选时保留现有配置）",
              "Replace receiver configuration (otherwise keep existing)",
            )}
          </label>
        ) : (
          <p className="configuration-submit-wide">
            {t(
              "新渠道默认禁用，保存后可以测试和启用。",
              "New channels are disabled. Test and enable after saving.",
            )}
          </p>
        )}
        {replace && kind !== "email" ? (
          <>
            <label className="configuration-submit-wide">
              Webhook URL
              <input
                required
                type="url"
                value={url}
                disabled={busy}
                onChange={(e) => setURL(e.target.value)}
                placeholder="https://…"
              />
            </label>
            <label className="configuration-submit-wide">
              {t("JSON 请求体", "JSON payload")}
              <textarea
                required
                rows={7}
                value={payload}
                disabled={busy}
                onChange={(e) => setPayload(e.target.value)}
              />
            </label>
            <details className="configuration-submit-wide">
              <summary>
                {t("请求头和成功条件", "Headers and success condition")}
              </summary>
              <label>
                {t("请求头 JSON", "Headers JSON")}
                <textarea
                  rows={3}
                  value={headers}
                  onChange={(e) => setHeaders(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label>
                {t(
                  "成功字段（留空只判断 HTTP 2xx）",
                  "Success field (empty: HTTP 2xx only)",
                )}
                <input
                  value={successPath}
                  disabled={busy}
                  onChange={(e) => setSuccessPath(e.target.value)}
                  placeholder="errcode"
                />
              </label>
              <label>
                {t("期望值 JSON", "Expected JSON value")}
                <input
                  value={successValue}
                  disabled={busy}
                  onChange={(e) => setSuccessValue(e.target.value)}
                />
              </label>
              <p>
                {t("可用变量：", "Variables: ")}
                {
                  "{{status}}, {{title}}, {{summary}}, {{description}}, {{data_center_id}}, {{team_id}}, {{severity}}, {{alert_name}}, {{starts_at}}, {{ends_at}}, {{id}}"
                }
              </p>
            </details>
          </>
        ) : null}
        {replace && kind === "email" ? (
          <>
            <label>
              {t("SMTP 主机", "SMTP host")}
              <input
                required
                value={host}
                disabled={busy}
                onChange={(e) => setHost(e.target.value)}
              />
            </label>
            <label>
              {t("端口", "Port")}
              <input
                required
                type="number"
                min={1}
                max={65535}
                value={port}
                disabled={busy}
                onChange={(e) => setPort(Number(e.target.value))}
              />
            </label>
            <label>
              TLS
              <select
                value={tls}
                disabled={busy}
                onChange={(e) => setTLS(e.target.value)}
              >
                <option value="starttls">STARTTLS</option>
                <option value="implicit">TLS</option>
              </select>
            </label>
            <label>
              {t("SMTP 用户名", "SMTP username")}
              <input
                value={username}
                disabled={busy}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>
            <label>
              {t("SMTP 密码", "SMTP password")}
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <label>
              {t("发件地址", "Sender address")}
              <input
                required
                type="email"
                value={from}
                disabled={busy}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="configuration-submit-wide">
              {t("收件地址（逗号分隔）", "Recipients (comma-separated)")}
              <input
                required
                value={to}
                disabled={busy}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </>
        ) : null}
        <label>
          {t("严重程度（留空匹配全部）", "Severity (empty: all)")}
          <input
            value={severity}
            maxLength={128}
            disabled={busy}
            onChange={(e) => setSeverity(e.target.value)}
            placeholder="critical"
          />
        </label>
        <label>
          {t("Team ID（可选）", "Team ID (optional)")}
          <input
            value={team}
            maxLength={128}
            disabled={busy}
            onChange={(e) => setTeam(e.target.value)}
          />
        </label>
        <label>
          {t("告警名称（可选）", "Alert name (optional)")}
          <input
            value={alertName}
            maxLength={128}
            disabled={busy}
            onChange={(e) => setAlertName(e.target.value)}
          />
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={resolved}
            disabled={busy}
            onChange={(e) => setResolved(e.target.checked)}
          />
          {t("接收恢复通知", "Send resolved notifications")}
        </label>
        <label className="configuration-submit-wide">
          {t("操作原因", "Reason")}
          <input
            required
            maxLength={512}
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <button
          className="primary-button configuration-submit-wide"
          disabled={busy || !reason.trim()}
        >
          {t("保存渠道", "Save channel")}
        </button>
      </form>
    </ManagementDialog>
  );
}
